import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowLeft, Settings, Bell, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ExecutiveDashboard from '../components/ExecutiveDashboard';
import AdvancedManagement from '../components/AdvancedManagement';

const ExecutiveDashboardPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userRole] = useState('admin'); // Esto vendría del contexto de autenticación

  const tabs = [
    {
      id: 'dashboard',
      name: 'Dashboard Ejecutivo',
      description: 'Vista general y KPIs del sistema',
      icon: '📊'
    },
    {
      id: 'management',
      name: 'Gestión Avanzada',
      description: 'Administración de datos y entidades',
      icon: '⚙️'
    }
  ];

  const handleBackClick = () => {
    navigate('/admin');
  };

  return (
    <>
      <Helmet>
        <title>Dashboard Ejecutivo - SCIAC</title>
        <meta name="description" content="Dashboard ejecutivo y gestión avanzada del Sistema de Control Integral Agrícola Comunal" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <button
                  onClick={handleBackClick}
                  className="mr-4 p-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Dashboard Ejecutivo</h1>
                  <p className="text-gray-600">Vista integral y gestión avanzada del sistema SISCIAC</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button className="p-2 text-gray-600 hover:text-gray-900 transition-colors">
                  <Bell className="w-5 h-5" />
                </button>
                <button className="p-2 text-gray-600 hover:text-gray-900 transition-colors">
                  <Settings className="w-5 h-5" />
                </button>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Admin</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{tab.icon}</span>
                    <div className="text-left">
                      <div>{tab.name}</div>
                      <div className="text-xs text-gray-400">{tab.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 sm:px-0">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'dashboard' && (
                <ExecutiveDashboard userRole={userRole} />
              )}
              {activeTab === 'management' && (
                <AdvancedManagement 
                  entityType="all" 
                  userRole={userRole} 
                />
              )}
            </motion.div>
          </div>
        </main>

        {/* Quick Actions Floating Button */}
        <div className="fixed bottom-6 right-6">
          <div className="flex flex-col space-y-3">
            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-lg shadow-lg border p-4 min-w-[200px]"
            >
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Resumen Rápido</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Productores:</span>
                  <span className="font-medium text-blue-600">156</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cultivos:</span>
                  <span className="font-medium text-green-600">89</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Activos:</span>
                  <span className="font-medium text-green-600">12</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
              className="bg-green-600 rounded-full p-4 shadow-lg hover:bg-green-700 transition-colors cursor-pointer"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExecutiveDashboardPage;
